/**
 * Landers or offers block: add row, weight + share %, edit / duplicate / remove.
 * For Media Devoted | Created By Ciro Autiero
 */

import { type FC, useMemo } from 'react';
import type { VoluumPathUpdate } from '@trafficpulse/shared';

import { getOffersLandersAi } from '@/features/flow-optimizer/tabs/standard/v2/utils/path-ai';
import { usePreviewMode } from '@/features/flow-optimizer/components/preview/PreviewIndexContext';
import { PreviewSortLabel, usePreviewMetricSort } from '@/features/flow-optimizer/components/preview/sort';
import { useTrackerAi } from '@/features/flow-optimizer/tracker';
import { type PathItemKind } from '@/features/flow-optimizer/tabs/standard/v2/utils';
import { usePathItemsUi } from './hooks';
import { TrackerLanderEditor } from '@/features/flow-optimizer/tracker-entities/TrackerLanderEditor';
import PathItemIdDialog from './PathItemIdDialog';
import { PathItemsRows } from './rows';
import {
  pathItemsCommitAdd,
  pathItemsCommitEdit,
  pathItemsDuplicateAt,
  pathItemsRemoveAt,
  pathItemsSetWeight,
} from './path-item-operations';
import {
  pathItemEntityNoun,
  readPathItemDisplayName,
  readPathItemEntityId,
} from './shared';
import { sumPathItemWeights } from './weight-share';

export type { PathItemKind };

export interface PathItemsSectionProps {
  kind: PathItemKind;
  path: VoluumPathUpdate;
  onPatch: (mutator: (p: VoluumPathUpdate) => void) => void;
}

const PathItemsSection: FC<PathItemsSectionProps> = ({
  kind,
  path,
  onPatch,
}) => {
  const items = (path[kind] ?? []) as unknown[];
  const weights = useMemo(
    () => items.map((raw) => (raw as { weight?: number }).weight ?? 0),
    [items],
  );
  const totalWeight = useMemo(() => sumPathItemWeights(weights), [weights]);
  const noun = pathItemEntityNoun(kind);
  const label = kind === 'landers' ? 'Landers' : 'Offers';
  const { ui, setUi, onEditRow, close } = usePathItemsUi(kind);
  const previewMode = usePreviewMode();
  const trackerAi = useTrackerAi();
  const { sort, cycle } = usePreviewMetricSort();

  const applyCommit = (
    mode: 'add' | 'edit',
    index: number | undefined,
    payload: { entityId: string; displayName: string },
  ) => {
    if (mode === 'add') {
      pathItemsCommitAdd(onPatch, kind, payload);
      return;
    }
    if (index === undefined) return;
    pathItemsCommitEdit(onPatch, kind, index, payload);
  };

  const handleRemove = (idx: number, title: string) => {
    if (window.confirm(`Remove this ${noun} from the path?\n${title}`)) {
      pathItemsRemoveAt(onPatch, kind, idx);
    }
  };

  let dialogIndex: number | undefined;
  let initialId = '';
  let initialName = '';
  if (ui.tag === 'simple' && ui.mode === 'edit') {
    dialogIndex = ui.index;
    const row = items[ui.index ?? 0];
    if (row !== undefined) {
      initialId = readPathItemEntityId(row, kind);
      initialName = readPathItemDisplayName(row, kind);
    }
  }

  const landerIx =
    kind === 'landers' && ui.tag === 'lander' ? ui.index : null;
  const itemWeightsLocked = trackerAi && getOffersLandersAi(path);

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          {label}
        </span>
        <button
          type="button"
          onClick={() => setUi({ tag: 'simple', mode: 'add' })}
          className="text-xs font-semibold text-primary hover:underline"
        >
          + Add {noun}
        </button>
      </div>
      {previewMode && items.length > 0 ? (
        // Current / New / Diff labels for the preview cells. Grid + actions
        // placeholder mirror PathItemRow's preview layout so columns line up.
        <div className="grid w-full items-center gap-x-1.5 px-1.5 grid-cols-[1.5rem_minmax(0,1fr)_minmax(12rem,auto)_auto]">
          <span />
          <span />
          <div className="flex items-center justify-end">
            <div className="grid grid-cols-[3rem_0.75rem_3rem_3.25rem] items-center gap-x-1.5">
              <PreviewSortLabel column="current" label="Current" sort={sort} onCycle={cycle} />
              <span />
              <PreviewSortLabel column="new" label="New" sort={sort} onCycle={cycle} />
              <PreviewSortLabel column="diff" label="Diff" sort={sort} onCycle={cycle} />
            </div>
          </div>
          <span className="w-[5.5rem]" aria-hidden />
        </div>
      ) : null}
      {items.length === 0 ? (
        <p className="text-xs italic text-muted-foreground">
          No {kind} configured.
        </p>
      ) : (
        <PathItemsRows
          items={items}
          kind={kind}
          noun={noun}
          pathId={path.id ?? ''}
          ariaLabel={label}
          sort={sort}
          totalWeight={totalWeight}
          itemWeightsLocked={itemWeightsLocked}
          onSetWeight={(idx, w) => pathItemsSetWeight(onPatch, kind, idx, w)}
          onEdit={onEditRow}
          onDuplicate={(idx) => pathItemsDuplicateAt(onPatch, kind, idx)}
          onRemove={handleRemove}
        />
      )}
      {landerIx !== null ? (
        <TrackerLanderEditor
          open
          onOpenChange={(o) => !o && close()}
          landerId={readPathItemEntityId(items[landerIx], kind)}
          rowIndex={landerIx}
          onPatch={onPatch}
        />
      ) : null}
      {ui.tag === 'simple' ? (
        <PathItemIdDialog
          open
          onOpenChange={(o) => !o && close()}
          mode={ui.mode}
          entityLabel={noun}
          initialEntityId={ui.mode === 'add' ? '' : initialId}
          initialDisplayName={ui.mode === 'add' ? '' : initialName}
          onCommit={(payload) => applyCommit(ui.mode, dialogIndex, payload)}
        />
      ) : null}
    </div>
  );
};

export default PathItemsSection;
